import { useCallback, useMemo } from "react";
import cloneDeep from "lodash/cloneDeep";
import { v4 as uuidv4 } from "uuid";
import { t } from "@/i18n-js/instance";
import { TextSelect } from "@/react/components/shared/TextSelect";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import type { NestedStyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { mergeStylesPreserving } from "@circle-react-shared/Emails/VisualEmailBuilder/Utils/mergeStylesPreserving";
import { BUTTON_DEFAULT_DATA, BUTTON_TYPE_OPTIONS } from "./constants";
import type { ButtonType } from "./constants";
import { isButtonType } from "./typePredicate";

interface ButtonTypePickerProps {
  label: string;
}

export const ButtonTypePicker = ({ label }: ButtonTypePickerProps) => {
  const data = useBuilderStore(state => state.getSelectedBlock());
  const selectedBlock = data?.block;
  const updateSelectedBlock = useBuilderStore(
    state => state.updateSelectedBlock,
  );

  const isButtonBlock = useMemo(
    () => !!selectedBlock && isButtonType(selectedBlock.type),
    [selectedBlock],
  );

  const handleButtonTypeChange = useCallback(
    (newType: ButtonType) => {
      if (!selectedBlock) return;

      const defaultData = BUTTON_DEFAULT_DATA[newType];

      const currentStylesValue = selectedBlock.props.styles?.value as
        | NestedStyleValue
        | undefined;
      const defaultStylesValue = defaultData.props.styles?.value as
        | NestedStyleValue
        | undefined;
      const mergedStylesValue = mergeStylesPreserving({
        current: currentStylesValue,
        defaults: defaultStylesValue,
        preserveKeys: ["margin", "padding", "sizing", "alignment", "textAlign"],
      });

      updateSelectedBlock({
        ...selectedBlock,
        id: uuidv4(),
        type: newType,
        meta: defaultData.meta,
        props: {
          ...selectedBlock.props,
          text: selectedBlock.props.text,
          buttonType: defaultData.props.buttonType
            ? cloneDeep(defaultData.props.buttonType)
            : selectedBlock.props.buttonType,
          styles: {
            prop: "styles" as const,
            type: "styles" as const,
            value: mergedStylesValue,
          },
        },
      });
    },
    [selectedBlock, updateSelectedBlock],
  );

  if (!isButtonBlock || !selectedBlock) return null;

  const buttonType = selectedBlock.type as ButtonType;

  return (
    <div>
      <label className="text-dark text-xs font-normal">{label}</label>
      <TextSelect
        options={BUTTON_TYPE_OPTIONS}
        value={buttonType}
        onChange={handleButtonTypeChange}
        placeholder={t("builder_ui.prop_type.button_type")}
        buttonClassName="w-full"
      />
    </div>
  );
};
